import { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Flex, Form, Input, message, Space } from 'antd';

import CommonModalFactory from '@/plugins/ModalFactory';
import SVGDeleteTime from '@/assets/imgs/deleteTime.svg?react';
import SVGIcon from '@/components/SVGIcon';
import { ColumnsType } from 'antd/es/table';
import { openDeleteModal } from '@/components/Modal/common';
import { useRequest, useSetState } from 'ahooks';
import { ECompactWordType, phraseApi, PhraseItemRes } from '@/axios/company';
import PhraseTable from '../PhraseTable';
import { CompactWordCollapse } from '../CompactWord';
import { ObjectKeys } from '@/utils';
import AccessButton from '@/components/AccessButton';
import OperatorBtns from '@/components/Table/operateBtns';
import EllipsisText from '@/components/Table/EllipsisText';
import { openBatchAddWordsModal } from '.';
import CommonTable from '@/components/Table';
import CustomSelect from '@/components/Base/Select';
import { EPublishStatus, getPublishStatusText, isPublishedStatus } from '@/constants/geo';

const CompactWordTable = ({
  compactWords,
  companyId,
}: {
  compactWords: { [key in ECompactWordType]: string[] };
  companyId: string;
}) => {
  // 搜索词组列表
  const [phraseParams, setPhraseParams] = useSetState<Parameters<typeof phraseApi.list>[0]>({
    pageIndex: 1,
    pageSize: 10,
    companyId,
    type: 'SEARCH_WORD',
  });
  const {
    data: phraseList,
    runAsync: refreshPhraseList,
    loading,
  } = useRequest(
    () => {
      return phraseApi.list(phraseParams);
    },
    {
      refreshDeps: [phraseParams],
    }
  );
  const columns: ColumnsType<PhraseItemRes> = [
    {
      title: '词包名称',
      dataIndex: 'name',
      width: 200,
    },
    {
      title: '搜索词个数',
      dataIndex: 'wordCount',
      width: 120,
    },
    {
      title: '词组类型',
      dataIndex: 'type',
      width: 120,
      render: (value) => {
        if (value === 'BRAND') {
          return '品牌词组';
        }
        if (value === 'SEARCH') {
          return '关键词组';
        }
        return value;
      },
    },
    {
      title: '搜索词摘要',
      dataIndex: 'categories',
      width: 360,
      render: (value, record) => {
        const text = record.categories
          ?.filter((row) => {
            return row.words && row.words.length > 0;
          })
          .map((item) => item.description + (item.words?.length ? `(${item.words?.join('、')})` : ''))
          .join('\n');
        return (
          <EllipsisText
            text={text}
            tooltip={
              <div
                style={{
                  whiteSpace: 'pre-wrap',
                }}
              >
                {text}
              </div>
            }
          />
        );
      },
    },
    {
      title: '发布状态',
      dataIndex: 'publishStatusText',
      width: 120,
      render: (_, record) => getPublishStatusText(record.publishStatus),
    },

    {
      title: '操作',
      width: 210,
      render: (_, record) => {
        return (
          <OperatorBtns
            btns={[
              {
                type: 'common',
                text: '复制',
                onClick: () => {
                  openCreateSearchPhraseModal(
                    {
                      compactWords,
                      companyId,
                      id: record.recordId,
                    },
                    undefined,
                    () => {
                      refreshPhraseList();
                    }
                  );
                },
                permission: ['company:search_word:component_word_record:create'],
              },
              {
                type: 'edit',
                permission: ['company:search_word:component_word_record:edit'],
                onClick: () => {
                  openEditSearchPhraseModal(
                    {
                      id: record.recordId,
                      name: record.name,
                    },
                    undefined,
                    () => {
                      refreshPhraseList();
                    }
                  );
                },
              },
              {
                type: 'common',
                text: isPublishedStatus(record.publishStatus) ? '取消发布' : '发布',
                permission: ['company:search_word:component_word_record:edit'],
                onClick: async () => {
                  if (isPublishedStatus(record.publishStatus)) {
                    await phraseApi.unpublish(record.recordId);
                    message.success('取消发布成功');
                  } else {
                    await phraseApi.publish(record.recordId);
                    message.success('发布成功');
                  }
                  refreshPhraseList();
                },
              },

              {
                type: 'delete',
                onClick: () => {
                  openDeleteModal(
                    {
                      content: '确认删除该搜索词组吗？',
                      api: async () => {
                        return await phraseApi.delete([record.recordId]);
                      },
                    },
                    undefined,
                    refreshPhraseList
                  );
                },
                permission: ['company:search_word:component_word_record:delete'],
              },
            ]}
          />
        );
      },
    },
  ];
  const [selectedRows, setSelectedRows] = useState<React.Key[]>([]);
  const [selectedRowRecords, setSelectedRowRecords] = useState<PhraseItemRes[]>([]);
  const hasSelectedRows = selectedRows.length > 0;
  const allSelectedPublished = useMemo(
    () => hasSelectedRows && selectedRowRecords.every((record) => isPublishedStatus(record.publishStatus)),
    [hasSelectedRows, selectedRowRecords]
  );
  const allSelectedUnpublished = useMemo(
    () => hasSelectedRows && selectedRowRecords.every((record) => !isPublishedStatus(record.publishStatus)),
    [hasSelectedRows, selectedRowRecords]
  );
  return (
    <div>
      <Flex gap={8} align="center" style={{ marginBottom: '20px' }}>
        {!hasSelectedRows && (
          <AccessButton permission={['company:search_word:component_word_record:create']}>
            <Button
              type="primary"
              onClick={() => {
                openCreateSearchPhraseModal(
                  {
                    compactWords,
                    companyId,
                  },
                  undefined,
                  (_) => {
                    setPhraseParams({ pageIndex: 1 });
                  }
                );
              }}
            >
              创建品牌词包
            </Button>
          </AccessButton>
        )}
        {hasSelectedRows && (
          <>
            <Button
              onClick={async () => {
                setSelectedRows([]);
                setSelectedRowRecords([]);
              }}
            >
              取消选择({selectedRows.length})
            </Button>
            <AccessButton permission={['company:search_word:component_word_record:edit']}>
              <Button
                onClick={async () => {
                  if (!allSelectedUnpublished) {
                    message.warning('批量发布时，选中的词包必须全是未发布状态');
                    return;
                  }
                  await Promise.all((selectedRows as string[]).map((recordId) => phraseApi.publish(recordId)));
                  message.success('批量发布成功');
                  setSelectedRows([]);
                  setSelectedRowRecords([]);
                  refreshPhraseList();
                }}
              >
                批量发布({selectedRows.length})
              </Button>
            </AccessButton>
            <AccessButton permission={['company:search_word:component_word_record:edit']}>
              <Button
                onClick={async () => {
                  if (!allSelectedPublished) {
                    message.warning('批量取消发布时，选中的词包必须全是已发布状态');
                    return;
                  }
                  await Promise.all((selectedRows as string[]).map((recordId) => phraseApi.unpublish(recordId)));
                  message.success('批量取消发布成功');
                  setSelectedRows([]);
                  setSelectedRowRecords([]);
                  refreshPhraseList();
                }}
              >
                批量取消发布({selectedRows.length})
              </Button>
            </AccessButton>
            <AccessButton permission={['company:search_word:component_word_record:delete']}>
              <Button
                type="primary"
                danger
                onClick={() => {
                  openDeleteModal(
                    {
                      content: `确定删除选中的${selectedRows.length}个词组吗？`,
                      api: () => phraseApi.delete(selectedRows as string[]),
                    },
                    undefined,
                    () => {
                      setPhraseParams({ pageIndex: 1 });
                      setSelectedRows([]);
                      setSelectedRowRecords([]);
                    }
                  );
                }}
              >
                批量删除({selectedRows.length})
              </Button>
            </AccessButton>
          </>
        )}
      </Flex>
      <CommonTable<PhraseItemRes>
        loading={loading}
        rowKey={'recordId'}
        columns={columns}
        dataSource={phraseList?.records || []}
        total={phraseList?.total}
        params={phraseParams}
        setParams={setPhraseParams}
        rowSelection={{
          preserveSelectedRowKeys: true,
          selectedRowKeys: selectedRows,
          onChange: (selectedRowKeys, selectedRecords) => {
            setSelectedRows(selectedRowKeys);
            setSelectedRowRecords(selectedRecords as PhraseItemRes[]);
          },
        }}
        scroll={{ x: '770px' }}
      />
    </div>
  );
};
export default CompactWordTable;
// 创建搜索词组合词弹窗
export const openCreateSearchPhraseModal = CommonModalFactory<{
  compactWords: { [key in ECompactWordType]: string[] };
  companyId: string;
  detail?: any;
  componentWords: { [key in ECompactWordType]: string[] };
}>(
  {
    title: '创建搜索词组',
    width: 1000,
    styles: {
      body: {
        maxHeight: '70vh',
        overflowY: 'auto',
        padding: '16px',
      },
    },
  },
  ({ data, setOkFn, update }) => {
    const [searchPhrase, setSearchPhrase] = useState<{
      [key in ECompactWordType]: string[];
    }>({
      [ECompactWordType.QuestionPrefix]: [],
      [ECompactWordType.LocationPrefix]: [],
      [ECompactWordType.RecommendPrefix]: [],
      [ECompactWordType.BrandPrefix]: [],
      [ECompactWordType.ScenePrefix]: [],
      [ECompactWordType.Core]: [],
      [ECompactWordType.EntitySuffix]: [],
      [ECompactWordType.RecommendSuffix]: [],
      [ECompactWordType.QuestionAnswerSuffix]: [],
    });
    const [form] = Form.useForm();
    const [publishToClient, setPublishToClient] = useState<EPublishStatus>(EPublishStatus.UNPUBLISHED);
    useEffect(() => {
      if (data?.componentWords) {
        setSearchPhrase(data?.componentWords);
      }
      if (data?.detail) {
        setSearchPhrase(data?.componentWords);
        form.setFieldsValue({
          name: data?.detail?.name,
        });
      }
    }, []);
    useEffect(() => {
      setOkFn(async () => {
        const values = await form.validateFields();
        const wordMap = Object.entries(searchPhrase).map(([key, words]) => ({
          type: key as ECompactWordType,
          words,
        }));
        await phraseApi.create({
          companyId: data?.companyId || '',
          name: values.name,
          words: values.words,
          wordMap,
          type: values.type,
          publishToClient,
        });
        message.success('组合词创建成功');
        return searchPhrase;
      });
    }, [searchPhrase, data?.detail, data?.companyId, form, publishToClient, setOkFn]);
    useEffect(() => {
      update({
        footer: (originNode) => (
          <Flex align="center" justify="space-between" style={{ width: '100%' }}>
            <div style={{ position: 'relative', zIndex: 1, display: 'inline-flex', alignItems: 'center' }}>
              <Checkbox
                checked={publishToClient === EPublishStatus.PUBLISHED}
                onChange={(e) => {
                  setPublishToClient(e.target.checked ? EPublishStatus.PUBLISHED : EPublishStatus.UNPUBLISHED);
                }}
              >
                发布到客户后台
              </Checkbox>
            </div>
            <div>{originNode}</div>
          </Flex>
        ),
      });
    }, [publishToClient, update]);
    {
      /* 各种类型勾选的词的个数相乘 */
    }
    const sum = useMemo(() => {
      const values = Object.values(searchPhrase);
      if (values.every((arr) => arr.length === 0)) {
        return 0;
      }
      return values.reduce((acc, arr) => acc * (arr.length || 1), 1);
    }, [searchPhrase]);
    return (
      <Form form={form} initialValues={{ type: 'SEARCH' }}>
        <Form.Item name="name" label="词组名称" rules={[{ required: true, message: '请输入词组名称' }]}>
          <Input placeholder="请输入词组名称" />
        </Form.Item>
        <Form.Item name="type" label="词组类型" initialValue={'SEARCH'} required>
          <CustomSelect
            options={[
              {
                label: '品牌词组',
                value: 'BRAND',
              },
              {
                label: '关键词组',
                value: 'SEARCH',
              },
            ]}
          />
        </Form.Item>


        <Flex gap={8}>
          <Flex vertical gap={8} style={{ flex: 1, background: '#F7F8FA', padding: '12px' }}>
            <div>搜索词组({sum}个)</div>
            <CompactWordCollapse
              compactWords={data?.compactWords}
              searchPhrase={searchPhrase}
              setSearchPhrase={setSearchPhrase}
            />
          </Flex>
          <Flex vertical gap={8} style={{ flex: 1 }}>
            <Space>
              添加词组
              <a
                onClick={() =>
                  openBatchAddWordsModal(undefined, undefined, (res) => {
                    const currentWords = form.getFieldValue('words') || [];
                    form.setFieldsValue({
                      words: [...currentWords, ...res],
                    });
                  })
                }
              >
                批量复制
              </a>
            </Space>
            <Form.List name="words">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Form.Item key={key} {...restField}>
                      <Flex gap={8} align="center">
                        <Form.Item noStyle name={name} rules={[{ required: true, message: '请输入词组' }]}>
                          <Input placeholder="请输入词组" />
                        </Form.Item>
                        <SVGIcon icon={<SVGDeleteTime />} onClick={() => remove(name)} />
                      </Flex>
                    </Form.Item>
                  ))}
                  <Form.Item>
                    <Button type="dashed" onClick={() => add()} block>
                      添加词组
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
            {data?.detail && (
              <div style={{ flex: 1, flexShrink: 0 }}>
                <PhraseTable recordId={data?.detail?.id as string} />
              </div>
            )}
          </Flex>
        </Flex>
      </Form>
    );
  },
  async ({ id, compactWords, ...props }) => {
    if (id) {
      const detail = await phraseApi.getDetail(id);
      const componentWordsAfterAddCore = JSON.parse(JSON.stringify(detail?.componentWords || {}));
      componentWordsAfterAddCore[ECompactWordType.Core] = detail?.coreWords || [];
      // 过滤掉已经不存在compactWords里面的词语
      ObjectKeys(componentWordsAfterAddCore).forEach((key) => {
        componentWordsAfterAddCore[key] = (componentWordsAfterAddCore[key] || []).filter((word: string) => {
          return (compactWords[key] || []).includes(word);
        });
      });
      return {
        ...props,
        id,
        compactWords,
        componentWords: componentWordsAfterAddCore,
      };
    }
    return {
      ...props,
      id,
      compactWords,
    };
  }
);

const openEditSearchPhraseModal = CommonModalFactory<{
  name: string;
  id: string;
  wordCount: number;
}>(
  {
    title: '编辑搜索词组',
    width: 1000,
  },
  ({ data, setOkFn }) => {
    const [form] = Form.useForm();
    useEffect(() => {
      form.setFieldsValue({
        name: data?.name,
      });
      setOkFn(async () => {
        const values = await form.validateFields();
        await phraseApi.update({
          recordId: data?.id || '',
          name: values.name,
          // type: values.type,
        });
        message.success('搜索词组更新成功');
      });
    }, [data]);
    return (
      <Form
        form={form}
        style={{
          maxHeight: '60vh',
          overflowY: 'auto',
          padding: '16px',
        }}
        labelCol={{ span: 4 }}
        layout="vertical"
      >
        <Form.Item name="name" label="词组名称" rules={[{ required: true, message: '请输入词组名称' }]}>
          <Input placeholder="请输入词组名称" />
        </Form.Item>
        {/* <Form.Item name="type" label="词组类型" required initialValue={"SEARCH"}>
          <CustomSelect placeholder="请选择词组类型" options={[
            {
              value:'BRAND',
              label:'品牌词组',
            }, {
              value:'SEARCH',
              label:'关键词组',
            }
          ]} />
        </Form.Item> */}
        <Flex gap={8}>
          <div style={{ flex: 1, flexShrink: 0 }}>
            <PhraseTable recordId={data?.id as string} />
          </div>
        </Flex>
      </Form>
    );
  },
  async ({ id, ...props }) => {
    const detail = await phraseApi.getDetail(id);
    return {
      ...props,
      id,
      wordCount: detail?.wordCount || 0,
    };
  }
);
